import { useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import { Copy, Grid3x3, ShieldOff, Sparkles, X, Save, Trash2, Download, Upload } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger, DropdownMenuSeparator, DropdownMenuLabel } from "@/components/ui/dropdown-menu";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import ConfirmDialog from "@/components/ConfirmDialog";
import api, { formatDetail } from "@/lib/api";
import { DIVISION_COLOR } from "@/lib/constants";

/* ═══════════════════════════════════════════════════════════════════════════
   POLICY PRESETS
   Mirror /app/backend/tune_matrix_policy.py — kept in-sync manually. Format
   compatible dengan matrix rows: perms & visibility per (division × level).
   ═══════════════════════════════════════════════════════════════════════════ */

const FULL = { active: true, create: true, update: true, delete: true, view: true };
const RUD  = { active: true, create: false, update: true, delete: true, view: true };
const CRU  = { active: true, create: true, update: true, delete: false, view: true };
const CV   = { active: true, create: true, update: false, delete: false, view: true };
const RV   = { active: true, create: false, update: false, delete: false, view: true };
const V    = { active: true, create: false, update: false, delete: false, view: true };

const ENTERPRISE_DEFAULT = {
  label: "Enterprise (Default)",
  description: "Locked-down: BOD-only untuk fitur sensitif (settings/access_control/user_management). HRGA & FAT jadi editor HR/Finance. Semua staff bisa akses fitur operasional dengan visibility scoped.",
  features: {
    user_management: { perms: {}, visibility: {} },
    access_control:  { perms: {}, visibility: {} },
    settings:        { perms: {}, visibility: {} },
    personnel_manage: {
      perms: { BOD: { 1: FULL, 2: FULL, 3: RV }, HRGA: { 1: FULL, 2: RUD, 3: RV }, FAT: { 1: RV, 2: RV } },
      visibility: {},
    },
    payroll_print: {
      perms: { BOD: { 1: FULL, 2: FULL, 3: RV }, FAT: { 1: FULL, 2: RUD, 3: RV }, HRGA: { 1: FULL, 2: RUD, 3: RV } },
      visibility: { BOD: { 1: "all", 2: "all", 3: "own" }, FAT: { 1: "all", 2: "all", 3: "own" }, HRGA: { 1: "all", 2: "all", 3: "own" } },
    },
    announcements: {
      perms: {
        BOD: { 1: FULL, 2: FULL, 3: V },
        HRGA: { 1: CRU, 2: CRU, 3: V },
        FAT: { 1: CRU, 2: CRU, 3: V },
        "Procurement Asset Logistic": { 1: CV, 2: CV, 3: V },
        Project: { 1: CV, 2: CV, 3: V, 4: V },
      },
      visibility: {},
    },
    petty_cash: {
      perms: {
        BOD: { 1: FULL, 2: FULL, 3: CV },
        FAT: { 1: FULL, 2: RUD, 3: V },
        HRGA: { 1: V, 2: V },
        "Procurement Asset Logistic": { 1: CV, 2: CV, 3: CV },
        Project: { 1: CV, 2: CV, 3: CV, 4: CV },
      },
      visibility: {
        BOD: { 1: "all", 2: "all", 3: "own" },
        FAT: { 1: "all", 2: "all", 3: "own" },
        HRGA: { 1: "all", 2: "all" },
        "Procurement Asset Logistic": { 1: "own", 2: "own", 3: "own" },
        Project: { 1: "own", 2: "own", 3: "own", 4: "own" },
      },
    },
    dashboard:      { perms: allDivsLvV(3), visibility: {} },
    team:           { perms: allDivsLvV(3), visibility: {} },
    leave_calendar: { perms: allDivsLvV(3), visibility: {} },
    performance: {
      perms: allDivsLvV(3),
      visibility: scopedByLevel(),
    },
    tasks: {
      perms: {
        BOD: { 1: FULL, 2: FULL, 3: CRU },
        FAT: { 1: FULL, 2: CRU, 3: CV },
        HRGA: { 1: FULL, 2: CRU, 3: CV },
        "Procurement Asset Logistic": { 1: CRU, 2: CRU, 3: CV },
        Project: { 1: FULL, 2: CRU, 3: CV, 4: CV },
      },
      visibility: scopedByLevel(),
    },
    projects: {
      perms: {
        BOD: { 1: FULL, 2: FULL, 3: RV },
        FAT: { 1: FULL, 2: RUD, 3: V },
        HRGA: { 1: V, 2: V },
        "Procurement Asset Logistic": { 1: V, 2: V, 3: V },
        Project: { 1: CRU, 2: CRU, 3: V, 4: V },
      },
      visibility: {
        BOD: { 1: "all", 2: "all", 3: "division" },
        FAT: { 1: "all", 2: "all", 3: "division" },
        HRGA: { 1: "division", 2: "division" },
        "Procurement Asset Logistic": { 1: "division", 2: "division", 3: "own" },
        Project: { 1: "division_lower", 2: "division_lower", 3: "own", 4: "own" },
      },
    },
    leaves: {
      perms: {
        BOD: { 1: FULL, 2: FULL, 3: CV },
        FAT: { 1: FULL, 2: CV, 3: CV },
        HRGA: { 1: FULL, 2: FULL, 3: CV },
        "Procurement Asset Logistic": { 1: CV, 2: CV, 3: CV },
        Project: { 1: CRU, 2: CRU, 3: CV, 4: CV },
      },
      visibility: {
        BOD: { 1: "all", 2: "all", 3: "own" },
        FAT: { 1: "all", 2: "own", 3: "own" },
        HRGA: { 1: "all", 2: "all", 3: "own" },
        "Procurement Asset Logistic": { 1: "own", 2: "own", 3: "own" },
        Project: { 1: "division_lower", 2: "division_lower", 3: "own", 4: "own" },
      },
    },
  },
};

const LOCKED_DOWN = {
  label: "Locked-Down (Ketat)",
  description: "Reset semua fitur ke empty. Hanya BOD Lv1 (super-admin bypass) yang bisa akses. Berguna untuk audit ulang dari scratch.",
  features: Object.fromEntries(
    ["user_management","access_control","settings","personnel_manage","payroll_print",
     "announcements","petty_cash","dashboard","team","leave_calendar","performance",
     "tasks","projects","leaves"].map((k) => [k, { perms: {}, visibility: {} }])
  ),
};

const OPEN_DEV = {
  label: "Open (Development)",
  description: "PERINGATAN: Semua divisi Lv1-3 dapat CRUD penuh di semua fitur (view-only untuk Lv3). Hanya untuk lingkungan dev/testing — JANGAN dipakai di production.",
  features: Object.fromEntries(
    ["user_management","access_control","settings","personnel_manage","payroll_print",
     "announcements","petty_cash","dashboard","team","leave_calendar","performance",
     "tasks","projects","leaves"].map((k) => [k, {
      perms: allDivsLv(3, { 1: FULL, 2: FULL, 3: V }),
      visibility: {},
    }])
  ),
};

export const POLICY_PRESETS = { enterprise: ENTERPRISE_DEFAULT, locked: LOCKED_DOWN, open: OPEN_DEV };

function allDivsLvV(maxLevel) {
  const divs = ["BOD", "FAT", "HRGA", "Procurement Asset Logistic", "Project"];
  const out = {};
  for (const d of divs) {
    out[d] = {};
    for (let lv = 1; lv <= maxLevel; lv++) out[d][lv] = V;
  }
  return out;
}
function allDivsLv(maxLevel, cellPerLevel) {
  const divs = ["BOD", "FAT", "HRGA", "Procurement Asset Logistic", "Project"];
  const out = {};
  for (const d of divs) {
    out[d] = {};
    for (let lv = 1; lv <= maxLevel; lv++) out[d][lv] = cellPerLevel[lv] || V;
  }
  return out;
}
function scopedByLevel() {
  return {
    BOD: { 1: "all", 2: "all", 3: "division" },
    FAT: { 1: "all", 2: "division", 3: "own" },
    HRGA: { 1: "all", 2: "division", 3: "own" },
    "Procurement Asset Logistic": { 1: "division", 2: "division", 3: "own" },
    Project: { 1: "division", 2: "division_lower", 3: "own", 4: "own" },
  };
}

/* ═══════════════════════════════════════════════════════════════════════════
   AccessPolicyPresets — dropdown apply preset
   ═══════════════════════════════════════════════════════════════════════════ */

export function AccessPolicyPresets({ rows, setRows, disabled }) {
  const [confirm, setConfirm] = useState({ open: false, preset: null }); // preset: full object OR built-in key
  const [customs, setCustoms] = useState([]);
  const [saveDialog, setSaveDialog] = useState({ open: false, name: "", description: "", busy: false });
  const [deleteConfirm, setDeleteConfirm] = useState({ open: false, preset: null });
  const [importDialog, setImportDialog] = useState({ open: false, file: null, parsed: null, name: "", desc: "", busy: false, error: "" });

  const loadCustoms = async () => {
    try {
      const { data } = await api.get("/access-control-presets");
      setCustoms(data || []);
    } catch { /* silent — matrix.view yg gate */ }
  };
  useEffect(() => { loadCustoms(); }, []);

  const applyPreset = (preset) => {
    if (!preset || !preset.features) return;
    setRows((rows) => rows.map((r) => {
      const feat = preset.features[r.feature];
      if (!feat) return r;
      const normPerms = {};
      for (const [d, lvMap] of Object.entries(feat.perms || {})) {
        normPerms[d] = {};
        for (const [lv, cell] of Object.entries(lvMap)) normPerms[d][String(lv)] = { ...cell };
      }
      const normVis = {};
      for (const [d, lvMap] of Object.entries(feat.visibility || {})) {
        normVis[d] = {};
        for (const [lv, scope] of Object.entries(lvMap)) normVis[d][String(lv)] = scope;
      }
      return { ...r, perms: normPerms, visibility: normVis };
    }));
    toast.success(`Preset "${preset.label || preset.name}" di-stage. Klik "Simpan Semua" untuk apply.`);
  };

  // Save current matrix state as new custom preset. Serialise only division-level perms+visibility
  // per feature (mirror shape POLICY_PRESETS built-ins) — per-user overrides sengaja di-skip.
  const saveAsCustom = async () => {
    const name = saveDialog.name.trim();
    if (!name) { toast.error("Nama preset wajib diisi"); return; }
    const features = {};
    for (const r of rows) {
      const perms = {};
      for (const [d, lvMap] of Object.entries(r.perms || {})) {
        const cleaned = {};
        for (const [lv, cell] of Object.entries(lvMap)) {
          if (cell && cell.active) cleaned[lv] = { active: !!cell.active, create: !!cell.create, update: !!cell.update, delete: !!cell.delete, view: !!cell.view };
        }
        if (Object.keys(cleaned).length) perms[d] = cleaned;
      }
      const vis = {};
      for (const [d, lvMap] of Object.entries(r.visibility || {})) {
        const cleaned = {};
        for (const [lv, scope] of Object.entries(lvMap)) if (scope) cleaned[lv] = scope;
        if (Object.keys(cleaned).length) vis[d] = cleaned;
      }
      features[r.feature] = { perms, visibility: vis };
    }
    setSaveDialog((s) => ({ ...s, busy: true }));
    try {
      await api.post("/access-control-presets", { name, description: saveDialog.description.trim(), features });
      toast.success(`Preset "${name}" tersimpan`);
      setSaveDialog({ open: false, name: "", description: "", busy: false });
      await loadCustoms();
    } catch (e) { toast.error(formatDetail(e.response?.data?.detail) || "Gagal simpan preset"); setSaveDialog((s) => ({ ...s, busy: false })); }
  };

  const doDelete = async () => {
    if (!deleteConfirm.preset) return;
    try {
      await api.delete(`/access-control-presets/${deleteConfirm.preset.id}`);
      toast.success(`Preset "${deleteConfirm.preset.name}" dihapus`);
      setDeleteConfirm({ open: false, preset: null });
      await loadCustoms();
    } catch (e) { toast.error(formatDetail(e.response?.data?.detail) || "Gagal hapus preset"); }
  };

  // Export preset as JSON download — strip server IDs so payload portable across pods.
  const exportPreset = (cp) => {
    const portable = { _format: "ens-access-preset", _version: 1, name: cp.name, description: cp.description || "", features: cp.features || {} };
    const blob = new Blob([JSON.stringify(portable, null, 2)], { type: "application/json" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `ens-preset-${cp.name.replace(/[^a-zA-Z0-9]+/g, "-").toLowerCase()}.json`;
    a.click();
    URL.revokeObjectURL(url);
    toast.info(`Preset "${cp.name}" ter-download`);
  };

  const handleImportFile = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    try {
      const text = await file.text();
      const j = JSON.parse(text);
      if (j._format !== "ens-access-preset") throw new Error("File bukan preset ENS (missing _format tag)");
      if (!j.features || typeof j.features !== "object") throw new Error("File preset tak punya `features` object");
      setImportDialog({ open: true, file, parsed: j, name: j.name || "", desc: j.description || "", busy: false, error: "" });
    } catch (err) { toast.error(`Import gagal: ${err.message}`); }
    finally { e.target.value = ""; }
  };

  const confirmImport = async () => {
    const name = importDialog.name.trim();
    if (!name) { setImportDialog((s) => ({ ...s, error: "Nama wajib diisi" })); return; }
    setImportDialog((s) => ({ ...s, busy: true, error: "" }));
    try {
      await api.post("/access-control-presets", { name, description: importDialog.desc.trim(), features: importDialog.parsed.features });
      toast.success(`Preset "${name}" ter-import`);
      setImportDialog({ open: false, file: null, parsed: null, name: "", desc: "", busy: false, error: "" });
      await loadCustoms();
    } catch (e) { setImportDialog((s) => ({ ...s, busy: false, error: formatDetail(e.response?.data?.detail) || "Gagal import" })); }
  };

  const p = confirm.preset;

  return (
    <>
      <Button type="button" data-testid="btn-save-as-preset" disabled={disabled} onClick={() => setSaveDialog({ open: true, name: "", description: "", busy: false })} variant="outline" className="rounded-none h-8 text-[10px] uppercase tracking-wider disabled:opacity-40 inline-flex items-center gap-1 border-emerald-300 text-emerald-700 hover:bg-emerald-50">
        <Save size={11} /> Simpan sebagai Preset
      </Button>
      <label className="cursor-pointer">
        <input type="file" accept="application/json,.json" onChange={handleImportFile} className="hidden" data-testid="input-import-preset" />
        <span className={`inline-flex items-center gap-1 rounded-none h-8 px-3 text-[10px] uppercase tracking-wider border ${disabled ? "opacity-40 pointer-events-none" : "hover:bg-cyan-50"} border-cyan-300 text-cyan-700`}>
          <Upload size={11} /> Import JSON
        </span>
      </label>

      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <Button type="button" data-testid="btn-apply-policy-preset" disabled={disabled} variant="outline" className="rounded-none h-8 text-[10px] uppercase tracking-wider disabled:opacity-40 inline-flex items-center gap-1 border-indigo-300 text-indigo-700 hover:bg-indigo-50">
            <Sparkles size={11} /> Terapkan Preset
          </Button>
        </DropdownMenuTrigger>
        <DropdownMenuContent align="end" className="rounded-none w-80 max-h-96 overflow-y-auto">
          <DropdownMenuLabel className="text-[10px] uppercase tracking-wider text-slate-500">Preset Bawaan</DropdownMenuLabel>
          {Object.entries(POLICY_PRESETS).map(([k, ps]) => (
            <DropdownMenuItem key={k} data-testid={`preset-opt-${k}`} onSelect={() => setConfirm({ open: true, preset: { ...ps, _key: k } })} className="rounded-none flex-col items-start gap-0.5 py-2">
              <div className="text-xs font-semibold text-[#0B1D3A]">{ps.label}</div>
              <div className="text-[10px] text-slate-500 leading-tight">{ps.description}</div>
            </DropdownMenuItem>
          ))}

          {customs.length > 0 && <DropdownMenuSeparator />}
          {customs.length > 0 && (
            <DropdownMenuLabel className="text-[10px] uppercase tracking-wider text-emerald-700 flex items-center gap-1"><Save size={10} /> Preset Custom ({customs.length})</DropdownMenuLabel>
          )}
          {customs.map((cp) => (
            <div key={cp.id} className="flex items-start hover:bg-slate-50 group">
              <button
                type="button"
                data-testid={`preset-custom-${cp.id}`}
                onClick={() => setConfirm({ open: true, preset: { ...cp, label: cp.name, description: cp.description || `Preset custom dibuat oleh ${cp.created_by_name}` } })}
                className="flex-1 px-2 py-2 text-left"
              >
                <div className="text-xs font-semibold text-[#0B1D3A]">{cp.name}</div>
                <div className="text-[10px] text-slate-500 leading-tight line-clamp-2">{cp.description || <span className="italic text-slate-400">(tanpa deskripsi)</span>}</div>
                <div className="text-[9px] text-slate-400 mt-0.5">oleh {cp.created_by_name}</div>
              </button>
              <button
                type="button"
                data-testid={`btn-export-preset-${cp.id}`}
                onClick={(e) => { e.stopPropagation(); exportPreset(cp); }}
                className="px-2 py-2 text-slate-400 hover:text-cyan-600 opacity-0 group-hover:opacity-100 transition"
                title="Download preset sebagai JSON"
              ><Download size={11} /></button>
              <button
                type="button"
                data-testid={`btn-delete-preset-${cp.id}`}
                onClick={(e) => { e.stopPropagation(); setDeleteConfirm({ open: true, preset: cp }); }}
                className="px-2 py-2 text-slate-400 hover:text-rose-600 opacity-0 group-hover:opacity-100 transition"
                title="Hapus preset"
              ><Trash2 size={11} /></button>
            </div>
          ))}
        </DropdownMenuContent>
      </DropdownMenu>

      <ConfirmDialog
        open={confirm.open}
        onOpenChange={(o) => setConfirm({ ...confirm, open: o })}
        title={`Terapkan preset: ${p?.label ?? ""}`}
        description={`${p?.description ?? ""}\n\nPreset akan REPLACE division-level cells untuk fitur yang ada di preset. Per-user override (staff spesifik) TIDAK terkena. Perubahan akan di-stage dulu — klik "Simpan Semua" untuk commit.`}
        confirmLabel="Terapkan"
        onConfirm={() => { applyPreset(confirm.preset); setConfirm({ open: false, preset: null }); }}
      />

      <ConfirmDialog
        open={deleteConfirm.open}
        onOpenChange={(o) => setDeleteConfirm({ ...deleteConfirm, open: o })}
        title="Hapus Preset Custom"
        description={`Hapus preset "${deleteConfirm.preset?.name}" secara permanen? Preset yang sudah pernah di-apply tetap efektif — ini hanya menghapus template.`}
        confirmLabel="Hapus"
        variant="destructive"
        onConfirm={doDelete}
      />

      <Dialog open={importDialog.open} onOpenChange={(o) => !importDialog.busy && setImportDialog((s) => ({ ...s, open: o }))}>
        <DialogContent className="rounded-none max-w-md" data-testid="dlg-import-preset">
          <DialogHeader>
            <DialogTitle className="font-display text-lg flex items-center gap-2"><Upload size={16} className="text-cyan-600" /> Import Preset dari JSON</DialogTitle>
          </DialogHeader>
          <div className="space-y-3 text-sm">
            <div className="text-[11px] text-slate-500">
              Preset akan disimpan sebagai preset custom baru. Kalau nama sudah dipakai, ubah nama sebelum import. Features count: <b>{Object.keys(importDialog.parsed?.features || {}).length}</b>.
            </div>
            <div className="space-y-1">
              <Label className="overline text-slate-600 text-[10px]">Nama Preset *</Label>
              <Input data-testid="input-import-name" value={importDialog.name} onChange={(e) => setImportDialog((s) => ({ ...s, name: e.target.value }))} className="rounded-none h-9 text-xs" maxLength={80} />
            </div>
            <div className="space-y-1">
              <Label className="overline text-slate-600 text-[10px]">Deskripsi</Label>
              <Textarea data-testid="input-import-desc" value={importDialog.desc} onChange={(e) => setImportDialog((s) => ({ ...s, desc: e.target.value }))} className="rounded-none text-xs h-16" maxLength={500} />
            </div>
            {importDialog.error && <div className="text-[11px] text-rose-600 bg-rose-50 border border-rose-200 px-2 py-1">{importDialog.error}</div>}
          </div>
          <DialogFooter>
            <Button type="button" variant="outline" onClick={() => setImportDialog({ open: false, file: null, parsed: null, name: "", desc: "", busy: false, error: "" })} disabled={importDialog.busy} className="rounded-none h-8 text-[11px]">Batal</Button>
            <Button type="button" data-testid="btn-confirm-import" onClick={confirmImport} disabled={importDialog.busy || !importDialog.name.trim()} className="rounded-none h-8 text-[11px] bg-cyan-600 hover:bg-cyan-700 text-white">
              {importDialog.busy ? "Meng-import…" : "Import"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>


      <Dialog open={saveDialog.open} onOpenChange={(o) => !saveDialog.busy && setSaveDialog((s) => ({ ...s, open: o }))}>
        <DialogContent className="rounded-none max-w-md" data-testid="dlg-save-preset">
          <DialogHeader>
            <DialogTitle className="font-display text-lg flex items-center gap-2"><Save size={16} className="text-emerald-600" /> Simpan sebagai Preset Custom</DialogTitle>
          </DialogHeader>
          <div className="space-y-3 text-sm">
            <div className="text-[11px] text-slate-500">
              Snapshot matrix saat ini akan disimpan sebagai preset personal — muncul di dropdown "Terapkan Preset" untuk diterapkan kembali kapan saja. Hanya <b>division-level cells</b> yang disimpan; per-user override tidak ikut.
            </div>
            <div className="space-y-1">
              <Label className="overline text-slate-600 text-[10px]">Nama Preset *</Label>
              <Input data-testid="input-preset-name" value={saveDialog.name} onChange={(e) => setSaveDialog((s) => ({ ...s, name: e.target.value }))} placeholder="mis. Cabang Surabaya 2026" className="rounded-none h-9 text-xs" maxLength={80} />
            </div>
            <div className="space-y-1">
              <Label className="overline text-slate-600 text-[10px]">Deskripsi (opsional)</Label>
              <Textarea data-testid="input-preset-desc" value={saveDialog.description} onChange={(e) => setSaveDialog((s) => ({ ...s, description: e.target.value }))} placeholder="Contoh: Akses cabang Surabaya — Project Lv1-2 CRUD lokal, FAT view only" className="rounded-none text-xs h-20" maxLength={500} />
            </div>
          </div>
          <DialogFooter>
            <Button type="button" variant="outline" onClick={() => setSaveDialog({ open: false, name: "", description: "", busy: false })} disabled={saveDialog.busy} className="rounded-none h-8 text-[11px]">Batal</Button>
            <Button type="button" data-testid="btn-confirm-save-preset" onClick={saveAsCustom} disabled={saveDialog.busy || !saveDialog.name.trim()} className="rounded-none h-8 text-[11px] bg-emerald-600 hover:bg-emerald-700 text-white">
              {saveDialog.busy ? "Menyimpan…" : "Simpan Preset"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}

/* ═══════════════════════════════════════════════════════════════════════════
   AccessMatrixHeatmap — collapsible overview grid
   Warna cell: gray (empty) / merah (active only) / kuning (partial) / hijau (full CRUD).
   Klik cell → jump ke (div, level) tersebut.
   ═══════════════════════════════════════════════════════════════════════════ */

function cellColor(cell) {
  if (!cell || !cell.active) return "bg-slate-100 text-slate-300";
  const perms = ["create", "update", "delete", "view"];
  const on = perms.filter((p) => cell[p]).length;
  if (on === 0) return "bg-orange-200 text-orange-700"; // active tanpa perm (weird)
  if (on === 4) return "bg-emerald-500 text-white";     // full CRUD
  if (on === 1 && cell.view) return "bg-sky-200 text-sky-800"; // view only
  return "bg-amber-300 text-amber-900"; // partial
}
function cellLabel(cell) {
  if (!cell || !cell.active) return "";
  const map = { create: "C", update: "U", delete: "D", view: "V" };
  return ["create", "update", "delete", "view"].filter((p) => cell[p]).map((p) => map[p]).join("");
}

export function AccessMatrixHeatmap({ rows, allDivisions, activeDiv, activeLevel, onSelectCell }) {
  const [open, setOpen] = useState(false);
  const LV_LIST = [1, 2, 3, 4, 5];

  if (!open) {
    return (
      <div className="px-4 py-2 border-b border-slate-200 bg-slate-50">
        <Button type="button" data-testid="btn-open-heatmap" onClick={() => setOpen(true)} variant="outline" className="rounded-none h-7 text-[10px] uppercase tracking-wider inline-flex items-center gap-1 text-slate-600 hover:bg-white">
          <Grid3x3 size={11} /> Buka Overview Heatmap
        </Button>
        <span className="text-[10px] text-slate-500 italic ml-2">Lihat semua divisi × level dalam satu grid untuk audit cepat.</span>
      </div>
    );
  }

  return (
    <div className="px-4 py-3 border-b border-slate-200 bg-slate-50" data-testid="heatmap-panel">
      <div className="flex items-center justify-between mb-2">
        <div className="text-[11px] uppercase tracking-wider font-semibold text-slate-600 inline-flex items-center gap-1"><Grid3x3 size={12} /> Overview Heatmap</div>
        <div className="flex items-center gap-3">
          <div className="text-[10px] text-slate-500 flex items-center gap-2 flex-wrap">
            <span className="inline-flex items-center gap-1"><span className="w-3 h-3 bg-emerald-500" /> Full</span>
            <span className="inline-flex items-center gap-1"><span className="w-3 h-3 bg-amber-300" /> Partial</span>
            <span className="inline-flex items-center gap-1"><span className="w-3 h-3 bg-sky-200" /> View</span>
            <span className="inline-flex items-center gap-1"><span className="w-3 h-3 bg-orange-200" /> Active-Only</span>
            <span className="inline-flex items-center gap-1"><span className="w-3 h-3 bg-slate-100 border border-slate-200" /> Empty</span>
          </div>
          <button type="button" data-testid="btn-close-heatmap" onClick={() => setOpen(false)} className="p-1 hover:bg-white text-slate-500"><X size={13} /></button>
        </div>
      </div>
      <div className="overflow-x-auto">
        <table className="text-[10px] border-collapse">
          <thead>
            <tr>
              <th className="text-left px-2 py-1 sticky left-0 bg-slate-50">Fitur</th>
              {allDivisions.map((d) => (
                <th key={d} colSpan={LV_LIST.length} className="text-center px-1 py-1 border-b border-slate-200 font-semibold text-slate-700">{d}</th>
              ))}
            </tr>
            <tr>
              <th className="px-2 py-0.5 sticky left-0 bg-slate-50"></th>
              {allDivisions.flatMap((d) => LV_LIST.map((lv) => (
                <th key={`${d}-${lv}`} className="px-1 py-0.5 text-slate-500 font-normal border-b border-slate-200">L{lv}</th>
              )))}
            </tr>
          </thead>
          <tbody>
            {rows.map((r) => (
              <tr key={r.feature} className="border-t border-slate-100">
                <td className="px-2 py-1 font-semibold text-[#0B1D3A] sticky left-0 bg-slate-50 whitespace-nowrap">{r.label}</td>
                {allDivisions.flatMap((d) => LV_LIST.map((lv) => {
                  const cell = ((r.perms || {})[d] || {})[String(lv)];
                  const label = cellLabel(cell);
                  const color = cellColor(cell);
                  const isBod1 = d === "BOD" && lv === 1;
                  const isActive = d === activeDiv && lv === activeLevel;
                  return (
                    <td key={`${r.feature}-${d}-${lv}`} className="p-0.5">
                      <button
                        type="button"
                        data-testid={`heatmap-cell-${r.feature}-${d}-${lv}`}
                        onClick={() => onSelectCell(d, lv)}
                        title={isBod1 ? "BOD Lv1 super-admin (bypass)" : `${d} Lv${lv} → ${label || "empty"}`}
                        className={`w-8 h-5 inline-flex items-center justify-center text-[8px] font-mono font-bold transition-transform hover:scale-125 ${isBod1 ? "bg-[#0B1D3A] text-white" : color} ${isActive ? "ring-2 ring-[#FF5A1F] ring-offset-1" : ""}`}
                      >{isBod1 ? "★" : label || "·"}</button>
                    </td>
                  );
                }))}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

/* ═══════════════════════════════════════════════════════════════════════════
   LegacyOverridesPanel — surface per-user access_overrides + revoke button
   ═══════════════════════════════════════════════════════════════════════════ */

export function LegacyOverridesPanel({ users, onReload }) {
  const overridden = useMemo(() => (users || []).filter((u) => {
    const ao = u.access_overrides || {};
    return Object.keys(ao).length > 0;
  }), [users]);
  const [busy, setBusy] = useState(false);
  const [confirm, setConfirm] = useState({ open: false, uid: null, feature: null, uname: null });

  if (overridden.length === 0) return null;

  const revoke = async (uid, feature) => {
    setBusy(true);
    try {
      await api.patch(`/admin/users/${uid}/access-override`, { feature, allow: null });
      toast.success(`Override "${feature}" dicabut`);
      await onReload?.();
    } catch (e) { toast.error(formatDetail(e.response?.data?.detail) || "Gagal cabut override"); }
    finally { setBusy(false); }
  };

  return (
    <div className="border-b border-amber-200 bg-amber-50" data-testid="legacy-overrides-panel">
      <div className="px-4 py-2 flex items-center justify-between gap-3 flex-wrap">
        <div className="inline-flex items-center gap-2 text-[11px] text-amber-800">
          <ShieldOff size={13} />
          <span><b>{overridden.length} pegawai</b> punya legacy access override — grant explicit yang meng-override matrix normal. Review & cabut kalau tidak diperlukan.</span>
        </div>
      </div>
      <div className="overflow-x-auto max-h-52 overflow-y-auto">
        <table className="w-full text-[11px]">
          <thead className="bg-amber-100 text-[10px] uppercase tracking-wider text-amber-900">
            <tr>
              <th className="text-left px-3 py-1.5">Pegawai</th>
              <th className="text-left px-3 py-1.5">Divisi</th>
              <th className="text-left px-3 py-1.5">Fitur Override</th>
              <th className="text-right px-3 py-1.5 w-24">Aksi</th>
            </tr>
          </thead>
          <tbody>
            {overridden.map((u) => {
              const entries = Object.entries(u.access_overrides || {});
              return entries.map(([feat, val], i) => (
                <tr key={`${u.id}-${feat}`} className="border-t border-amber-200/60">
                  {i === 0 && (
                    <>
                      <td rowSpan={entries.length} className="px-3 py-1.5 align-top font-semibold text-[#0B1D3A]">{u.name}</td>
                      <td rowSpan={entries.length} className="px-3 py-1.5 align-top">
                        <span className={`px-1.5 py-0.5 text-[9px] font-semibold uppercase tracking-wider ${DIVISION_COLOR[u.division] || "bg-slate-600 text-white"}`}>{u.division} Lv{u.level}</span>
                      </td>
                    </>
                  )}
                  <td className="px-3 py-1.5">
                    <span className={`inline-block px-1.5 py-0.5 text-[10px] font-mono ${val ? "bg-emerald-100 text-emerald-800" : "bg-rose-100 text-rose-800"}`}>{feat} = {String(val)}</span>
                  </td>
                  <td className="px-3 py-1.5 text-right">
                    <Button
                      type="button"
                      data-testid={`btn-revoke-override-${u.id}-${feat}`}
                      onClick={() => setConfirm({ open: true, uid: u.id, feature: feat, uname: u.name })}
                      disabled={busy}
                      variant="outline"
                      className="rounded-none h-6 text-[10px] uppercase tracking-wider text-rose-700 border-rose-300 hover:bg-rose-50 disabled:opacity-40"
                    >Cabut</Button>
                  </td>
                </tr>
              ));
            })}
          </tbody>
        </table>
      </div>
      <ConfirmDialog
        open={confirm.open}
        onOpenChange={(o) => setConfirm({ ...confirm, open: o })}
        title="Cabut Legacy Override"
        description={`Cabut override "${confirm.feature}" untuk ${confirm.uname}? Akses fitur akan kembali mengikuti rule matrix normal (divisi + level).`}
        confirmLabel="Cabut Override"
        onConfirm={() => { revoke(confirm.uid, confirm.feature); setConfirm({ open: false, uid: null, feature: null, uname: null }); }}
      />
    </div>
  );
}

/* ═══════════════════════════════════════════════════════════════════════════
   Feature filter + Copy From (compact inline component)
   ═══════════════════════════════════════════════════════════════════════════ */

export function FeatureFilterAndCopy({ filter, setFilter, allDivisions, activeDiv, activeLevel, onCopy, disabled }) {
  const [copyDialog, setCopyDialog] = useState(false);
  const [srcDiv, setSrcDiv] = useState("");
  const [srcLevel, setSrcLevel] = useState(1);
  const LV_LIST = [1, 2, 3, 4, 5];

  const doCopy = () => {
    if (!srcDiv) { toast.error("Pilih divisi sumber"); return; }
    if (srcDiv === activeDiv && srcLevel === activeLevel) { toast.error("Sumber sama dengan target"); return; }
    onCopy(srcDiv, srcLevel);
    toast.info(`Perms disalin dari ${srcDiv} Lv${srcLevel} → ${activeDiv} Lv${activeLevel} (di-stage; klik Simpan Semua)`);
    setCopyDialog(false);
  };

  return (
    <div className="flex items-center gap-2 px-4 py-2 border-b border-slate-200 bg-white flex-wrap" data-testid="access-filter-bar">
      <Input
        data-testid="input-feature-filter"
        value={filter}
        onChange={(e) => setFilter(e.target.value)}
        placeholder="🔍 Cari fitur (mis: petty, project, task)…"
        className="rounded-none h-7 text-[11px] max-w-xs"
      />
      {filter && (
        <button type="button" onClick={() => setFilter("")} className="text-[10px] uppercase tracking-wider text-slate-500 hover:text-slate-700">Reset</button>
      )}
      <div className="flex-1" />
      <Button
        type="button"
        data-testid="btn-open-copy-from"
        onClick={() => setCopyDialog(true)}
        disabled={disabled}
        variant="outline"
        className="rounded-none h-7 text-[10px] uppercase tracking-wider disabled:opacity-40 inline-flex items-center gap-1"
      ><Copy size={11} /> Copy From…</Button>

      {copyDialog && (
        <div className="fixed inset-0 z-50 bg-black/50 flex items-center justify-center p-4" data-testid="copy-from-dialog">
          <div className="bg-white border border-slate-200 max-w-md w-full">
            <div className="px-4 py-3 border-b border-slate-200 flex items-center justify-between">
              <div className="font-display text-sm tracking-tight">Copy Perms Antar (Divisi × Level)</div>
              <button type="button" onClick={() => setCopyDialog(false)} className="text-slate-500 hover:text-slate-800"><X size={14} /></button>
            </div>
            <div className="p-4 space-y-3">
              <div className="text-[10px] text-slate-500">Salin seluruh perms + visibility dari <b>sumber</b> ke target aktif <b>{activeDiv} Lv{activeLevel}</b>. Berlaku ke SEMUA 14 fitur. Perubahan di-stage, belum di-simpan.</div>
              <div className="grid grid-cols-2 gap-2">
                <div>
                  <label className="text-[10px] uppercase tracking-wider text-slate-500">Divisi Sumber</label>
                  <Select value={srcDiv} onValueChange={setSrcDiv}>
                    <SelectTrigger data-testid="copy-src-div" className="rounded-none h-8 text-[11px]"><SelectValue placeholder="Pilih…" /></SelectTrigger>
                    <SelectContent>{allDivisions.map((d) => <SelectItem key={d} value={d}>{d}</SelectItem>)}</SelectContent>
                  </Select>
                </div>
                <div>
                  <label className="text-[10px] uppercase tracking-wider text-slate-500">Level Sumber</label>
                  <Select value={String(srcLevel)} onValueChange={(v) => setSrcLevel(Number(v))}>
                    <SelectTrigger data-testid="copy-src-lv" className="rounded-none h-8 text-[11px]"><SelectValue /></SelectTrigger>
                    <SelectContent>{LV_LIST.map((lv) => <SelectItem key={lv} value={String(lv)}>Lv{lv}</SelectItem>)}</SelectContent>
                  </Select>
                </div>
              </div>
            </div>
            <div className="px-4 py-3 border-t border-slate-200 flex justify-end gap-2">
              <Button type="button" onClick={() => setCopyDialog(false)} variant="outline" className="rounded-none h-8 text-[11px]">Batal</Button>
              <Button type="button" data-testid="btn-do-copy" onClick={doCopy} className="rounded-none h-8 text-[11px] bg-[#FF5A1F] hover:bg-[#e04a13] text-white">Salin</Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
